import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { ORG_DB_NAMES } from '../../config';
import { fmtDate, useFetch } from '../../hooks';
import { Alert, Badge, Button, Field, Input, Select, Textarea } from '../../ui/basics';
import { Drawer, useToast } from '../../ui/overlay';
import { dayFromNow, rules, useFormValidation } from '../../validation';
import PlacementForm from '../../components/PlacementForm';
import { docFile } from './docs';

export const BACKED_OUT = 'Backed out';
export const CONVERTED = 'Converted';
export const REASONS = ['Accepted another offer', 'Personal reasons', 'Compensation', 'Client cancelled', 'Unreachable', 'Other'];
const TONES = { Converted: 'accent', 'Backed out': 'danger', 'Documents complete': 'success', 'Collecting documents': 'warning' };

/** The candidate's status, plus a flag while a reinstatement waits for approval. */
export const StatusBadge = ({ row }) => (
  <span className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
    <Badge tone={TONES[row.onboarding_status] || ''}>{row.onboarding_status || '—'}</Badge>
    {row.reinstate_requested_at && <Badge tone="accent">Reinstatement pending</Badge>}
  </span>
);

const BACKOUT_SCHEMA = {
  reason: [rules.required('Reason'), rules.oneOf(REASONS, 'Reason')],
  note: [rules.maxLength(1000, 'Note'), rules.check((v, all) => all.reason !== 'Other' || String(v || '').trim() !== '', 'Add a note when the reason is Other.')],
  backout_date: rules.date('Backout date', { max: dayFromNow(1) }),
};

function BackoutDialog({ row, onClose, onDone }) {
  const toast = useToast();
  const [form, setForm] = useState({ reason: '', note: '', backout_date: dayFromNow(0) });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const v = useFormValidation(form, BACKOUT_SCHEMA);
  const bind = (key) => ({ value: form[key], onChange: (e) => setForm({ ...form, [key]: e.target.value }), ...v.props(key) });
  const save = async () => {
    setError('');
    if (!v.check()) return;
    setBusy(true);
    try {
      onDone(await api.post('Onboard_backout/', { onboard_id: row.onboard_id, ...form }));
      toast('Marked as backed out', 'success');
      onClose();
    } catch (e) { setError(v.fail(e) ? 'The server found a problem with the highlighted fields.' : e.message); setBusy(false); }
  };
  return (
    <Drawer center narrow title={`Mark ${row.candidate_name} as backed out`} onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="danger" loading={busy} onClick={save}>Mark as backed out</Button></>}>
      <div className="stack">
        {(v.summary || error) && <Alert>{error || v.summary}</Alert>}
        <Alert tone="warning">This stops all document reminders for this candidate and removes them from the pending counts in Analytics. A copy of the reminders is kept, so they come back if the candidate is reinstated.</Alert>
        <Field label="Reason *" error={v.shown('reason')}><Select placeholder="Select a reason" options={REASONS} {...bind('reason')} /></Field>
        <Field label="Backout date *" error={v.shown('backout_date')}><Input type="date" {...bind('backout_date')} /></Field>
        <Field label={form.reason === 'Other' ? 'Note *' : 'Note'} error={v.shown('note')}><Textarea maxLength={1000} {...bind('note')} /></Field>
      </div>
    </Drawer>
  );
}

function ReasonDialog({ row, onClose, onDone }) {
  const toast = useToast();
  const [form, setForm] = useState({ reason: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const v = useFormValidation(form, { reason: [rules.required('Reason'), rules.maxLength(1000, 'Reason')] });
  const save = async () => {
    setError('');
    if (!v.check()) return;
    setBusy(true);
    try {
      onDone(await api.post('Onboard_reinstate_request/', { onboard_id: row.onboard_id, reason: form.reason }));
      toast('Sent for approval', 'success');
      onClose();
    } catch (e) { setError(v.fail(e) ? 'The server found a problem with the highlighted field.' : e.message); setBusy(false); }
  };
  return (
    <Drawer center narrow title="Request reinstatement" onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={busy} onClick={save}>Send for approval</Button></>}>
      <div className="stack">
        {(v.summary || error) && <Alert>{error || v.summary}</Alert>}
        <Alert tone="info">An administrator other than you must approve this. Once approved, the candidate is active again and their reminders are restored.</Alert>
        <Field label="Why should this candidate be reinstated? *" error={v.shown('reason')}>
          <Textarea maxLength={1000} value={form.reason} onChange={(e) => setForm({ reason: e.target.value })} {...v.props('reason')} />
        </Field>
      </div>
    </Drawer>
  );
}

/** Backout and reinstatement controls for one candidate. `onResult` receives each server reply (its `data` is the updated candidate). */
export function OfferActions({ row, onResult }) {
  const { user, hasPage, org } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [dialog, setDialog] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const backedOut = row.onboarding_status === BACKED_OUT;
  const converted = row.onboarding_status === CONVERTED;
  const docs = row.required_documents || [];
  const complete = docs.length > 0 && docs.every((d) => docFile(d));
  const canConvert = !backedOut && !converted && user.can_edit && hasPage('placements') && (complete || user.is_admin);
  const pending = Boolean(row.reinstate_requested_at);
  const mine = (row.reinstate_requested_by || '').toLowerCase() === (user.email || '').toLowerCase();
  const orgKey = Object.keys(ORG_DB_NAMES).find((k) => ORG_DB_NAMES[k] === row.organization) || org;
  const lookups = useFetch(orgKey && orgKey !== 'combined' ? 'lookups/' : null, { organization: orgKey });
  const execs = lookups.data ? lookups.data.executives || [] : [];
  const assign = async (email) => {
    setError(''); setBusy('assign');
    try { const res = await api.post('Onboard_assign/', { onboard_id: row.onboard_id, onboarding_executive: email }); onResult(res); toast(res.message, 'success'); }
    catch (e) { setError(e.message); }
    setBusy('');
  };
  const sendMail = async () => {
    setError(''); setBusy('mail');
    try { const res = await api.post('Onboard_send_email/', { onboard_id: row.onboard_id }); onResult(res); toast(res.message, res.email_status === 'sent' ? 'success' : 'info'); }
    catch (e) { setError(e.message); }
    setBusy('');
  };
  const decide = async (decision) => {
    setError(''); setBusy(decision);
    try { const res = await api.post('Onboard_reinstate_decision/', { onboard_id: row.onboard_id, decision }); onResult(res); toast(res.message, 'success'); }
    catch (e) { setError(e.message); }
    setBusy('');
  };
  return (
    <div className="stack">
      {error && <Alert>{error}</Alert>}
      {backedOut && (
        <Alert tone="warning">
          <b>Backed out on {fmtDate(row.backout_date)}</b> · {row.backout_reason}{row.backed_out_by ? ` · recorded by ${row.backed_out_by}` : ''}
          {row.backout_note && <div style={{ marginTop: 4 }}>{row.backout_note}</div>}
          <div style={{ marginTop: 4 }}>No reminders are sent for this candidate.</div>
        </Alert>
      )}
      {backedOut && pending && (
        <Alert tone="info">
          <b>Reinstatement requested</b> by {row.reinstate_requested_by} on {fmtDate(row.reinstate_requested_at)}: {row.reinstate_reason}
          {hasPage('users') && !mine && user.can_edit ? (
            <div className="row" style={{ marginTop: 8 }}>
              <Button variant="primary" size="sm" loading={busy === 'approve'} onClick={() => decide('approve')}>Approve reinstatement</Button>
              <Button size="sm" loading={busy === 'reject'} onClick={() => decide('reject')}>Reject</Button>
            </div>
          ) : <div style={{ marginTop: 4 }}>{mine ? 'Waiting for another administrator to approve.' : 'Waiting for an administrator to approve.'}</div>}
        </Alert>
      )}
      {!backedOut && !converted && user.can_edit && (
        <Alert tone={row.offer_email_status === 'failed' ? undefined : 'info'}>
          <b>Offer email: </b>
          {row.offer_email_status === 'sent' ? 'sent ' + fmtDate(row.offer_email_at) + '. ' : row.offer_email_status ? 'not sent. ' : 'not sent yet. '}
          {row.offer_email_note}
          <div style={{ marginTop: 6 }}><Button size="sm" loading={busy === 'mail'} onClick={sendMail}>{row.offer_email_status === 'sent' ? 'Send again' : row.offer_email_status ? 'Retry' : 'Send offer email'}</Button></div>
        </Alert>
      )}
      {!backedOut && !converted && user.can_edit && (
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <Field label="Onboarding executive" className="grow" hint="Gets the document due-date reminders. Empty means the person who added the offer.">
            <Select value={row.onboarding_executive || ''} disabled={busy === 'assign' || !execs.length} onChange={(e) => assign(e.target.value)}
              placeholder={'Added by ' + (row.user || 'creator')} options={execs.map((e) => ({ value: e.email, label: e.name }))} aria-label="Onboarding executive" />
          </Field>
        </div>
      )}
      {converted && (
        <Alert tone="info">
          <b>Converted to a placement{row.converted_on ? ` on ${fmtDate(row.converted_on)}` : ''}.</b>
          {hasPage('placements') && <div style={{ marginTop: 6 }}><Button size="sm" onClick={() => navigate('/placements')}>Open placements</Button></div>}
        </Alert>
      )}
      {!backedOut && !converted && user.can_edit && hasPage('placements') && !complete && (
        <Alert tone="info">{user.is_admin ? 'Documents are still missing. As an administrator you can convert anyway, with a reason.' : 'Collect every required document before converting this offer to a placement.'}</Alert>
      )}
      {!backedOut && !converted && user.can_edit && (
        <div className="row">
          {canConvert && <Button variant="primary" onClick={() => setDialog('convert')}>Convert to placement</Button>}
          <Button variant="danger" onClick={() => setDialog('backout')}>Mark as backed out</Button>
        </div>
      )}
      {user.can_edit && backedOut && !pending && <div><Button onClick={() => setDialog('reinstate')}>Request reinstatement</Button></div>}
      {dialog === 'convert' && (
        <PlacementForm offer={row} defaultOrg={org} onClose={() => setDialog('')}
          onSaved={(res) => { setDialog(''); onResult({ data: { ...row, onboarding_status: CONVERTED, placement_id: res.data.s_no, converted_on: new Date().toISOString() } }); }} />
      )}
      {dialog === 'backout' && <BackoutDialog row={row} onClose={() => setDialog('')} onDone={onResult} />}
      {dialog === 'reinstate' && <ReasonDialog row={row} onClose={() => setDialog('')} onDone={onResult} />}
    </div>
  );
}
